import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Sparkles, X, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import ChatMessagesList from './chat/ChatMessagesList';
import ChatLeadCapture from './chat/ChatLeadCapture';
import useLang from '../hooks/useLang';

const API_BASE = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8001' : '');
const STORAGE_KEY = 'auditorialegal_chat_session';
const LEAD_SHOWN_KEY = 'auditorialegal_chat_lead_shown';
const LEAD_SENT_KEY = 'auditorialegal_chat_lead_sent';
const LEAD_TRIGGER_AT = 3; // show lead-capture after the 3rd assistant reply

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const translations = {
  es: {
    welcome: '¡Hola! Soy el asistente virtual de Auditorialegal.es. Puedo orientarte sobre el ENS, NIS2, RGPD y nuestra metodología pericial. ¿En qué puedo ayudarte?',
    suggestions: [
      '¿Qué es la certificación ENS?',
      '¿Aplica NIS2 a mi empresa?',
      '¿Cómo funciona el Diagnóstico Express?',
    ],
    launcherClose: 'Cerrar asistente IA',
    launcherOpen: 'Abrir asistente IA',
    title: 'Asistente Auditorialegal',
    status: 'Online · IA · Solo orientativo',
    closeAria: 'Cerrar',
    leadSuccess: '¡Perfecto! Joaquín te contactará en menos de 24 horas. Mientras tanto puedo seguir respondiéndote.',
    leadError: 'No se pudo registrar el contacto. Inténtalo más tarde.',
    leadInvalidEmail: 'Email no válido',
    leadConfirmed: 'Contacto registrado — Joaquín te llamará pronto',
    inputPlaceholder: 'Escribe tu pregunta...',
    inputAria: 'Escribe tu mensaje',
    sendAria: 'Enviar mensaje',
    errDefault: 'Conexión interrumpida. Inténtalo de nuevo.',
    replyErr: 'No he podido procesar tu mensaje.'
  },
  en: {
    welcome: 'Hello! I am the virtual assistant for Auditorialegal.es. I can guide you on ENS, NIS2, GDPR, and our expert forensic audit methodology. How can I help you?',
    suggestions: [
      'What is ENS certification?',
      'Does NIS2 apply to my company?',
      'How does the Express Assessment work?',
    ],
    launcherClose: 'Close AI Assistant',
    launcherOpen: 'Open AI Assistant',
    title: 'Auditorialegal Assistant',
    status: 'Online · AI · Informational only',
    closeAria: 'Close',
    leadSuccess: 'Perfect! Joaquín will contact you in less than 24 hours. Meanwhile, feel free to ask me more questions.',
    leadError: 'Could not register contact details. Please try again later.',
    leadInvalidEmail: 'Invalid email address',
    leadConfirmed: 'Contact registered — Joaquín will contact you soon',
    inputPlaceholder: 'Type your question...',
    inputAria: 'Type your message',
    sendAria: 'Send message',
    errDefault: 'Connection interrupted. Please try again.',
    replyErr: 'Could not process your message.'
  }
};

const countAssistantReplies = (messages) =>
  messages.filter((m) => m.role === 'assistant').length - 1;

export const ChatWidget = () => {
  const lang = useLang();
  const t = translations[lang];

  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showLead, setShowLead] = useState(false);
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [leadError, setLeadError] = useState('');
  const bodyRef = useRef(null);

  // Initialize and reset messages when language changes
  useEffect(() => {
    setMessages([
      {
        id: newId(),
        role: 'assistant',
        content: t.welcome,
      }
    ]);
  }, [lang, t.welcome]);

  useEffect(() => {
    let sid = sessionStorage.getItem(STORAGE_KEY);
    if (!sid) {
      sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem(STORAGE_KEY, sid);
    }
    setSessionId(sid);
    setLeadDone(sessionStorage.getItem(LEAD_SENT_KEY) === '1');
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, loading, showLead]);

  useEffect(() => {
    if (open && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [open]);

  const sendMessage = useCallback(
    async (text) => {
      const userMsg = (text || '').trim();
      if (!userMsg || loading) return;
      setError('');
      setMessages((prev) => [...prev, { id: newId(), role: 'user', content: userMsg }]);
      setInput('');
      setLoading(true);
      try {
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const chatUrl = isLocal 
          ? 'https://www.auditorialegal.es/api/chat/index.php' 
          : `${window.location.origin}/api/chat/index.php`;
        const res = await axios.post(
          chatUrl,
          { session_id: sessionId, message: userMsg },
          { timeout: 30000 }
        );
        const reply = res.data?.reply || t.replyErr;
        setMessages((prev) => {
          const next = [...prev, { id: newId(), role: 'assistant', content: reply }];
          const assistantCount = countAssistantReplies(next);
          const shouldShowLead =
            assistantCount >= LEAD_TRIGGER_AT &&
            !sessionStorage.getItem(LEAD_SHOWN_KEY) &&
            !leadDone;
          if (shouldShowLead) {
            sessionStorage.setItem(LEAD_SHOWN_KEY, '1');
            setTimeout(() => setShowLead(true), 600);
          }
          return next;
        });
      } catch (err) {
        const detail = err.response?.data?.detail;
        setError(detail || t.errDefault);
      } finally {
        setLoading(false);
      }
    },
    [loading, sessionId, leadDone, t.replyErr, t.errDefault]
  );

  const submitLead = useCallback(
    async (emailVal, extras = {}) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailVal || '')) {
        setLeadError(t.leadInvalidEmail);
        return;
      }
      setLeadSubmitting(true);
      setLeadError('');
      try {
        const ctx = messages
          .slice(-6)
          .map((m) => `${m.role === 'user' ? '👤' : '🤖'} ${m.content}`)
          .join('\n');
        const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        const leadUrl = isLocal 
          ? 'https://www.auditorialegal.es/api/contact/index.php' 
          : `${window.location.origin}/api/contact/index.php`;
        await axios.post(
          leadUrl,
          { 
            nombre: `Lead Chatbot: ${emailVal}`,
            email: emailVal, 
            mensaje: `Historial de chat:\n${ctx}`, 
            website: extras.website || '' 
          },
          { timeout: 15000 }
        );
        sessionStorage.setItem(LEAD_SENT_KEY, '1');
        setLeadDone(true);
        setShowLead(false);
        setMessages((prev) => [
          ...prev,
          {
            id: newId(),
            role: 'assistant',
            content: `${t.leadSuccess} (${emailVal})`,
          },
        ]);
      } catch (err) {
        const detail = err.response?.data?.detail;
        setLeadError(detail || t.leadError);
      } finally {
        setLeadSubmitting(false);
      }
    },
    [messages, t.leadInvalidEmail, t.leadSuccess, t.leadError]
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const showSuggestions = messages.length <= 1 && !loading;

  return (
    <>
      {/* Floating launcher (bottom-right, desktop only) */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? t.launcherClose : t.launcherOpen}
        aria-expanded={open}
        data-testid="chat-launcher"
        className="fixed bottom-[calc(72px+env(safe-area-inset-bottom,0px))] right-4 sm:right-6 z-[9999] w-12 h-12 rounded-full flex items-center justify-center shadow-[0_10px_30px_rgba(27,42,74,0.5)] border-2 border-[#D4AF37] bg-gradient-to-br from-[#1B2A4A] to-[#2a3b66] hover:scale-110 hover:shadow-[0_0_30px_rgba(212,175,55,0.6)] transition-all duration-300 group"
      >
        <Sparkles
          className="w-6 h-6 text-[#D4AF37] group-hover:rotate-12 transition-transform"
          strokeWidth={2.25}
        />
        {!open && (
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75" />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#D4AF37]" />
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-[calc(150px+env(safe-area-inset-bottom,0px))] right-4 sm:right-6 z-[9999] w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-12rem))] rounded-2xl overflow-hidden shadow-2xl border border-[#1B2A4A]/15 bg-white flex flex-col"
          role="dialog"
          aria-label="Asistente virtual Auditorialegal"
          data-testid="chat-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#1B2A4A] to-[#2a3b66] text-white">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-full bg-[#D4AF37]/15 flex items-center justify-center border border-[#D4AF37]/40">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div className="leading-tight">
                <p className="font-semibold text-sm">{t.title}</p>
                <p className="text-[11px] text-white/70 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
                  {t.status}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.closeAria}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div
            ref={bodyRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#f8fafc]"
            data-testid="chat-body"
          >
            <ChatMessagesList messages={messages} loading={loading} error={error} />

            {showSuggestions && (
              <div className="pt-2 flex flex-wrap gap-2">
                {t.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => sendMessage(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-[#1B2A4A]/15 bg-white text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-white transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {showLead && !leadDone && (
              <ChatLeadCapture
                onSubmit={submitLead}
                onClose={() => setShowLead(false)}
                submitting={leadSubmitting}
                error={leadError}
              />
            )}
            {leadDone && messages.length > 2 && (
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5">
                <CheckCircle2 className="w-3 h-3" /> {t.leadConfirmed}
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="flex items-center gap-2 px-3 py-3 border-t border-slate-200 bg-white"
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.inputPlaceholder}
              disabled={loading}
              maxLength={500}
              data-testid="chat-input"
              className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]/30 bg-slate-50"
              aria-label={t.inputAria}
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              aria-label={t.sendAria}
              data-testid="chat-send"
              className="w-10 h-10 rounded-lg bg-[#1B2A4A] hover:bg-[#2a3b66] text-[#D4AF37] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
